import React, { useState, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import moment from "moment";
import { serverImgUrl } from "../config/constants";

interface attendanceList {
  attendanceDate: string;
  latitude: string;
  longitude: string;
  photo: string;
}

interface Doctor {
  doctorId: number;
  fmrNo: string;
  doctorName: string;
  latitude: string;
  longitude: string;
  createdon: string;
  contactNumber: string;
  email: string;
  attendanceList: attendanceList[];
}

interface Props {
  doctors: Doctor[];
  eventDates: string[];
  onCapture: (doctorId: number, doctorName: string, eventDate: any) => void;
}

const RegisteredDoctorsTable: React.FC<Props> = ({
  doctors,
  eventDates,
  onCapture,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [doctorsPerPage, setDoctorsPerPage] = useState(15);

  /* 🔍 FILTER BY CONTACT NUMBER */
  const filteredDoctors = useMemo(() => {
    if (!search.trim()) return doctors;
    return doctors.filter((doc) =>
      doc.contactNumber?.includes(search.trim())
    );
  }, [search, doctors]);

  /* 📄 PAGINATION */
  const totalPages = Math.ceil(filteredDoctors.length / doctorsPerPage);
  const displayedDoctors = filteredDoctors.slice(
    (page - 1) * doctorsPerPage,
    page * doctorsPerPage
  );

  return (
    <>
      {/* 🔍 SEARCH BAR */}
      <div className="mb-3 d-flex align-items-center flex-wrap gap-3 w-100">
        <input
          type="text"
          className="form-control"
          placeholder="Search by mobile number"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // reset page on search
          }}
          style={{ maxWidth: "280px" }}
        />
        <div className="d-flex align-items-center gap-2 ms-auto">
          <Form.Label className="mb-0 fw-semibold">Rows per page</Form.Label>
          <Form.Select
            size="sm"
            value={doctorsPerPage}
            onChange={(e) => {
              setDoctorsPerPage(Number(e.target.value));
              setPage(1);
            }}
            style={{ width: "auto" }}
          >
            {[10, 15, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            <th>Doctor Name</th>
            <th>FMR No</th>
            <th>Registered On</th>
            <th>Email</th>
            <th>Mobile No</th>
            <th>Photos</th>
          </tr>
        </thead>

        <tbody>
          {displayedDoctors.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                No doctors found
              </td>
            </tr>
          ) : (
            displayedDoctors.map((doc) => (
              <tr key={doc.doctorId}>
                <td>{doc.doctorName}</td>
                <td>{doc.fmrNo}</td>
                <td>{doc.createdon}</td>
                <td>{doc.email}</td>
                <td>{doc.contactNumber}</td>

                <td>
                  <div className="d-flex gap-2 flex-wrap">
                    {eventDates.map((eventDate, dayIndex) => {
                      const photoEntry = (doc.attendanceList || []).find(
                        (p) =>
                          normalizeToDisplayDate(p.attendanceDate) === eventDate
                      );

                      return (
                        <div
                          key={dayIndex}
                          className="d-flex flex-column align-items-center gap-1"
                          style={{ width: 90 }}
                        >
                          {photoEntry?.photo ? (
                            <img
                              src={`${serverImgUrl}/photos/${photoEntry.photo}`}
                              alt="attendance"
                              width={80}
                              height={80}
                              className="border rounded"
                            />
                          ) : (
                            <div
                              className="border rounded d-flex align-items-center justify-content-center text-muted"
                              style={{
                                width: 80,
                                height: 80,
                                backgroundColor: "#f8f9fa",
                                fontSize: "0.85rem",
                              }}
                            >
                              No Photo
                            </div>
                          )}
                          <small>{eventDate}</small>
                        </div>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* 📄 PAGINATION */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-end align-items-center gap-2 py-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            « First
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            ‹ Prev
          </Button>
          <Form.Select
            size="sm"
            value={page}
            onChange={(e) => setPage(Number(e.target.value))}
            style={{ width: "auto" }}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i} value={i + 1}>
                Page {i + 1} of {totalPages}
              </option>
            ))}
          </Form.Select>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next ›
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
          >
            Last »
          </Button>
        </div>
      )}
    </>
  );
};

export default RegisteredDoctorsTable;
  const normalizeToDisplayDate = (dateStr: string) => {
    const m = moment(
      dateStr,
      ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601],
      true
    );
    return m.isValid() ? m.format("DD-MM-YYYY") : dateStr;
  };
